import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { User } from '../interface/user';
import { TourConfigService } from './tour-config.service';
import { ProjectService } from './project.service';
import { TrialOnboardingHelpActionsService } from './trial-onboarding-help-actions.service';

interface ApiResponse<T> {
  message: string;
  data: T;
  status: boolean;
}

type DataRequest =
  | 'EmployeeList'
  | 'TeamList'
  | 'ApplicationUsage'
  | 'WebsiteUsage'
  | 'ApplicationLogs'
  | 'TimeLine'
  | 'UserReport'
  | 'ReportUserWise'
  | 'AbsenceCalendar'
  | 'ReportUsageSummary'
  | 'ReportDailyTrends'
  | 'ReportDataTable'
  | 'ReportDataTableMatrix'
  | 'ReportPerWebsite';

/**
 * Legacy empty-account dashboard sample fixtures.
 * Do NOT use this for the guided Example workday (`/setup/example-workday`).
 * Callers must only apply fixtures when real API data is empty — never overwrite successful payloads.
 */
@Injectable({
  providedIn: 'root',
})
export class DummyDataService {
  userData!: User;

  private isShowingDemoDataSubject: BehaviorSubject<boolean>;
  isShowingDemoData$: Observable<boolean>;

  constructor(
    private _tourConfigService: TourConfigService,
    private _projectService: ProjectService,
    private trialOnboardingHelp: TrialOnboardingHelpActionsService,
    private router: Router
  ) {
    const isDemoOptIn = this.trialOnboardingHelp.isShowingDemoData();
    this.isShowingDemoDataSubject = new BehaviorSubject<boolean>(isDemoOptIn);
    this.isShowingDemoData$ = this.isShowingDemoDataSubject.asObservable();
  }

  setDemoDataVisible(isVisible: boolean): void {
    this.isShowingDemoDataSubject.next(isVisible);
  }

  get isDemoDataVisible(): boolean {
    return this.isShowingDemoDataSubject.getValue();
  }

  /** Exit legacy sample mode and keep banner/subject in sync. */
  clearLegacySampleMode(): void {
    this.trialOnboardingHelp.clearStaleSampleOptIn({ hasRealEmployeeData: true });
    this.setDemoDataVisible(false);
  }

  set setUserData(data: User) {
    this.userData = data;
  }

  get getUserData(): User {
    return this.userData;
  }

  /**
   * Legacy fixtures may be offered only when the user explicitly opted in,
   * is not on a paid plan, and is not inside `/setup/*`.
   * Callers must still refuse to replace non-empty real API responses.
   */
  async availableConditions(): Promise<boolean> {
    if (this.router.url.startsWith('/setup')) {
      return false;
    }

    let userDetails = this.userData != undefined && this.userData != null;
    let userMeta = userDetails && this.userData.meta != null;

    if (!userDetails) {
      try {
        this.userData = await this.gettingUserDataIfNotAvailable();
      } catch {
        return false;
      }
    }

    userDetails = this.userData != undefined && this.userData != null;
    userMeta = userDetails && this.userData.meta != null;

    const quotaStatus = userMeta ? String(this.userData.meta.quota_status || '').toLowerCase() : '';
    const isPaid = quotaStatus === 'active';

    if (isPaid) {
      if (this.trialOnboardingHelp.isShowingDemoData()) {
        this.clearLegacySampleMode();
      }
      return false;
    }

    return this.trialOnboardingHelp.isShowingDemoData();
  }

  gettingUserDataIfNotAvailable(): Promise<User> {
    return new Promise((res, rej) => {
      this._projectService.getUser().subscribe(
        (response) => {
          res(response);
        },
        (err) => {
          console.log('Error while getting user data', err);
          rej(null);
        }
      );
    });
  }

  /**
   * Returns legacy fixtures when opted in. status:false means "do not use sample".
   * Never use the returned data to replace a successful non-empty API response.
   */
  async checkIsAvailableDemoData<T>(queryParam: DataRequest): Promise<ApiResponse<T>> {
    const conditionChecking = await this.availableConditions();
    if (!conditionChecking) {
      return {
        message: `No sample data for ${queryParam}`,
        data: [] as T,
        status: false,
      };
    } else if (!this.userData) {
      return { message: `No User Data Found`, data: [] as T, status: false };
    }

    return this.getDataByParams<T>(queryParam);
  }

  /**
   * Safe gate for callers: only use fixtures when real payload is empty AND sample is available.
   */
  async shouldUseLegacySampleFallback(realDataEmpty: boolean): Promise<boolean> {
    if (!realDataEmpty) {
      return false;
    }
    const available = await this.availableConditions();
    return available;
  }

  getDataByParams<T>(queryParam: DataRequest): ApiResponse<T> {
    switch (queryParam) {
      case 'EmployeeList': {
        const data = this._tourConfigService.getEmployeeListJson();
        return {
          message: `${queryParam} data found`,
          data: data as T,
          status: true,
        };
      }
      case 'TeamList': {
        const teamData = this._tourConfigService.getTeamProjectJsonData();
        return {
          message: `${queryParam} data found`,
          data: teamData as T,
          status: true,
        };
      }
      case 'ApplicationUsage': {
        const appUsageData = this._tourConfigService.getApplicationUsageJsonDataForDashboard();
        return {
          message: `${queryParam} data found`,
          data: appUsageData as T,
          status: true,
        };
      }
      case 'WebsiteUsage': {
        const websiteUsageData = this._tourConfigService.getWebsiteUsageJsonDataForWebsite();
        return {
          message: `${queryParam} data found`,
          data: websiteUsageData as T,
          status: true,
        };
      }
      case 'ApplicationLogs': {
        const applicationLogs = this._tourConfigService.getApplicationUsageJsonData();
        return {
          message: `${queryParam} data found`,
          data: applicationLogs as T,
          status: true,
        };
      }
      case 'TimeLine': {
        const timeLineData = this._tourConfigService.getTimelineJsonData();
        return {
          message: `${queryParam} data found`,
          data: timeLineData as T,
          status: true,
        };
      }
      case 'UserReport': {
        const userReport = this._tourConfigService.getUserReportJson();
        return {
          message: `${queryParam} data found`,
          data: userReport as T,
          status: true,
        };
      }
      case 'ReportUserWise': {
        const userReportUserWise = this._tourConfigService.getUserWiseReportsJson();
        return {
          message: `${queryParam} data found`,
          data: userReportUserWise as T,
          status: true,
        };
      }
      case 'AbsenceCalendar': {
        const absenceCalendar = this._tourConfigService.getAbsenceCalendarJson();
        return {
          message: `${queryParam} data found`,
          data: absenceCalendar as T,
          status: true,
        };
      }
      case 'ReportUsageSummary':
        return {
          message: `${queryParam} data found`,
          data: this._tourConfigService.getReportUsageSummaryJson() as T,
          status: true,
        };
      case 'ReportDailyTrends':
        return {
          message: `${queryParam} data found`,
          data: this._tourConfigService.getReportDailyTrendsJson() as T,
          status: true,
        };
      case 'ReportDataTable':
        return {
          message: `${queryParam} data found`,
          data: this._tourConfigService.getReportDataTableJson() as T,
          status: true,
        };
      case 'ReportDataTableMatrix':
        return {
          message: `${queryParam} data found`,
          data: this._tourConfigService.getReportDataTableMatrixJson() as T,
          status: true,
        };
      case 'ReportPerWebsite':
        return {
          message: `${queryParam} data found`,
          data: this._tourConfigService.getReportPerWebsiteJson() as T,
          status: true,
        };
      default:
        return { message: `No Data Found for ${queryParam}`, data: [] as T, status: false };
    }
  }
}
